import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CODING_AGENTS, buildIdentityPrompt, type CodingAgentKey } from './identityPrompt';
import ClaudeIcon from '../icons/claude.png';
import CodexIcon from '../icons/codex.png';
import CursorLightIcon from '../icons/cursor-light.svg';
import CursorDarkIcon from '../icons/cursor-dark.svg';

/** Official product marks. Cursor ships light/dark app icons; swap with the theme. */
function AgentMark({ agent, className }: { agent: CodingAgentKey; className?: string }) {
  if (agent === 'cursor') {
    return (
      <>
        <img src={CursorLightIcon} alt="" aria-hidden className={cn('dark:hidden', className)} />
        <img src={CursorDarkIcon} alt="" aria-hidden className={cn('hidden dark:block', className)} />
      </>
    );
  }
  return <img src={agent === 'claude' ? ClaudeIcon : CodexIcon} alt="" aria-hidden className={className} />;
}

/**
 * One row: the why, three coding-agent buttons, a link to read the prompt.
 * Clicking a button copies the prompt (Cursor also deep-links). Kept to a
 * single line of height so the Connect step never scrolls.
 */
export function StartNowSection({ mcpUrl, productName }: { mcpUrl: string; productName: string }) {
  const [copied, setCopied] = useState<CodingAgentKey | null>(null);
  const [open, setOpen] = useState(false);
  const prompt = buildIdentityPrompt({ mcpUrl, productName });

  const copyPrompt = async (): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(prompt);
      return true;
    } catch {
      setOpen(true);
      toast.error('Could not copy automatically. Copy the prompt from the dialog.');
      return false;
    }
  };

  const handOff = async (key: CodingAgentKey) => {
    const agent = CODING_AGENTS.find((entry) => entry.key === key)!;
    if (!(await copyPrompt())) return;
    setCopied(key);
    window.setTimeout(() => setCopied((current) => (current === key ? null : current)), 2500);
    if (agent.deepLink) window.location.assign(agent.deepLink(prompt));
    toast.success(agent.hint);
  };

  return (
    <div className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold">Start now: add Aimdoc identity to your server</div>
        <div className="truncate text-xs text-muted-foreground">
          Needed before services run for your customers. Hand it to your coding agent while Aimdoc builds.{' '}
          <button type="button" onClick={() => setOpen(true)} className="font-medium text-blue-600 hover:underline dark:text-blue-400">
            See the prompt
          </button>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {CODING_AGENTS.map((agent) => {
          const isCopied = copied === agent.key;
          return (
            <button
              key={agent.key}
              type="button"
              title={agent.deepLink ? `Open ${agent.name} with the prompt` : `Copy the prompt for ${agent.name}`}
              aria-label={agent.name}
              onClick={() => handOff(agent.key)}
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg border transition-all',
                isCopied
                  ? 'border-green-600/40 bg-green-600/5 text-green-700'
                  : 'border-border bg-background text-foreground hover:border-blue-500/35 hover:bg-muted/30'
              )}
            >
              {isCopied ? <Check className="h-4 w-4" /> : <AgentMark agent={agent.key} className="h-6 w-6 rounded-[7px]" />}
            </button>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Prompt for your coding agent</DialogTitle>
            <DialogDescription>
              Adds Aimdoc identity verification to {productName || 'your'} MCP server. Set AIMDOC_IDENTITY_SECRET from
              Integrations, Identity Verification, when it asks.
            </DialogDescription>
          </DialogHeader>
          <pre className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-4 font-mono text-[12px] leading-[18px]">
            {prompt}
          </pre>
          <div className="flex justify-end">
            <Button type="button" onClick={async () => (await copyPrompt()) && toast.success('Prompt copied.')}>
              Copy prompt
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
